"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import {
	AhaApplySignupCard,
	markAhaApplyAskSkipped,
	wasAhaApplyAskSkipped,
} from "@/components/jobs/aha-apply-signup-card";
import {
	exposureFiredToday,
	markExposureFiredToday,
} from "@/lib/analytics/use-experiment";
import { useSession } from "@/lib/auth/client";
import {
	AHA_SIGNUP_ASK_CLICKED_EVENT,
	AHA_SIGNUP_ASK_FLAG,
	AHA_SIGNUP_ASK_SHOWN_EVENT,
	type AhaSignupAskArm,
	hasSeenAhaSignupAsk,
	markAhaSignupAskSeen,
	readAhaSignupAskArm,
	readAhaSignupAskArmNow,
} from "@/lib/experiments/aha-signup-ask";
import {
	claimApplyMomentForIntentSignup,
	JOB_INTENT_SIGNUP_SRC,
} from "@/lib/experiments/job-intent-signup";

/**
 * `aha-signup-ask`, feed trigger (`feed_apply`, 2026-11-01): the same
 * post-apply signup card as job detail (`JobIntentSignup`), after a resolved
 * anonymous guest clicks an external Apply on a /jobs feed card. PostHog
 * 2026-10-30..31: 4 of 10 proven-human anonymous apply-clickers applied only
 * from the feed and were never exposed. See `src/lib/experiments/aha-signup-ask.ts`.
 *
 * The feed card does NOT dispatch `ld:apply-clicked` (its job-detail
 * listeners would react to a feed click); it dispatches its own event, after
 * the employer tab has opened, so apply is never blocked or delayed.
 *
 * The signup return goes to the job's detail page with
 * `?src=job_intent_signup`, where `JobIntentSignup`'s return leg saves the
 * job: the card's "save this job" promise is kept by the existing path.
 */

export const FEED_APPLY_CLICKED_EVENT = "ld:feed-apply-clicked";

/** Called from the feed card's external Apply click handler. */
export function dispatchFeedApplyClicked(jobId: string): void {
	try {
		window.dispatchEvent(
			new CustomEvent(FEED_APPLY_CLICKED_EVENT, { detail: { jobId } }),
		);
	} catch {
		/* no window / CustomEvent: no ask, apply untouched */
	}
}

type Props = {
	jobId: string;
	/** Locale-correct, slug-bearing detail path (no query). */
	detailPath: string;
	locale: string;
};

export function FeedAhaSignupAsk({ jobId, detailPath, locale }: Props) {
	const posthog = usePostHog();
	const { data: session, isPending } = useSession();
	// Resolved guest only: never while the session check is in flight.
	const resolvedGuest = !isPending && !session?.user;
	const evLocale = locale === "en" ? "en" : "id";
	const [shown, setShown] = useState(false);
	const liveRef = useRef(resolvedGuest);
	liveRef.current = resolvedGuest;

	const callbackURL = `${detailPath}?src=${JOB_INTENT_SIGNUP_SRC}`;

	// Arm read AT THE APPLY CLICK, never on mount (same rule as job detail).
	useEffect(() => {
		if (!posthog) return;
		let cancelled = false;
		const expose = (arm: AhaSignupAskArm) => {
			if (!exposureFiredToday(AHA_SIGNUP_ASK_FLAG)) {
				markExposureFiredToday(AHA_SIGNUP_ASK_FLAG);
				posthog.capture("experiment_exposure", {
					experiment: AHA_SIGNUP_ASK_FLAG,
					variant: arm,
					trigger: "feed_apply",
					job_id: jobId,
					surface: "feed_apply",
				});
			}
			// Once per person, across every trigger; a skip also counts.
			if (
				arm !== "treatment" ||
				wasAhaApplyAskSkipped() ||
				hasSeenAhaSignupAsk()
			) {
				return;
			}
			markAhaSignupAskSeen("feed_apply");
			claimApplyMomentForIntentSignup(jobId);
			setShown(true);
		};
		const onApply = (event: Event) => {
			const detail = (event as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId !== jobId) return;
			if (!liveRef.current) return;
			const sync = readAhaSignupAskArmNow(posthog);
			if (sync) {
				expose(sync);
				return;
			}
			void readAhaSignupAskArm(posthog).then((arm) => {
				if (!cancelled && arm) expose(arm);
			});
		};
		window.addEventListener(FEED_APPLY_CLICKED_EVENT, onApply);
		return () => {
			cancelled = true;
			window.removeEventListener(FEED_APPLY_CLICKED_EVENT, onApply);
		};
	}, [posthog, jobId]);

	useEffect(() => {
		if (!shown) return;
		posthog?.capture(AHA_SIGNUP_ASK_SHOWN_EVENT, {
			trigger: "feed_apply",
			job_id: jobId,
			locale: evLocale,
			placement: "feed_card",
		});
	}, [shown, posthog, jobId, evLocale]);

	if (!shown) {
		// e2e handle: a click before the session check settles is (correctly)
		// ignored, so specs wait for "resolved" before clicking Apply.
		return (
			<span
				hidden
				data-testid="feed-aha-signup-ask-state"
				data-auth-state={isPending ? "pending" : "resolved"}
			/>
		);
	}

	return (
		<AhaApplySignupCard
			locale={locale}
			callbackURL={callbackURL}
			showEyebrow
			testId="feed-aha-signup-ask"
			placement="feed_card"
			// Above the card's full-row overlay link (`absolute inset-0 z-0`).
			className="relative z-10"
			onAskClicked={(method) =>
				posthog?.capture(AHA_SIGNUP_ASK_CLICKED_EVENT, {
					trigger: "feed_apply",
					method,
					job_id: jobId,
					locale: evLocale,
					placement: "feed_card",
				})
			}
			onSkip={() => {
				markAhaApplyAskSkipped();
				setShown(false);
				posthog?.capture("job_intent_signup_skipped", {
					job_id: jobId,
					locale: evLocale,
					placement: "feed_card",
				});
			}}
		/>
	);
}
